import { Injectable, inject } from '@angular/core';
import { NeoDialog, NeoDialogRef } from '@neogov/pattern-library/neo-dialog';
import { firstValueFrom } from 'rxjs';
import {
  ConfirmationModalComponent,
  ConfirmationModalConfig,
  ConfirmationResult,
} from '../../../presentation/components/confirmation-modal';
import {
  BlockedConfirmationOptions,
  DeleteConfirmationOptions,
  DiscardConfirmationOptions,
  ToggleActiveConfirmationOptions,
  blockedConfirmation,
  deleteConfirmation,
  discardConfirmation,
  toggleActiveConfirmation,
} from './confirmation.utils';

/** Panel class emitted on every confirmation dialog. Hook for E2E — intentionally unstyled. */
export const CONFIRMATION_PANEL_CLASS = 'cm-confirmation-panel';

/**
 * ConfirmationProvider
 *
 * The single way to ask the user to confirm an action. Opens `ConfirmationModalComponent`
 * (the shared shell) with a declarative configuration, so every confirmation in the app looks
 * and behaves the same.
 *
 * @example Preset — the common case
 * ```typescript
 * const confirmed = await this._confirmation.delete({
 *   entity: 'department',
 *   name: department.name,
 *   autId: 'delete-department',
 * });
 * if (!confirmed) return;
 * ```
 *
 * @example Full control, including a third action
 * ```typescript
 * const result = await firstValueFrom(
 *   this._confirmation.open(deleteCaseTypeConfirmation(type, impact)).afterClosed(),
 * );
 * if (result === 'open-settings') { … }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ConfirmationProvider {
  private readonly _dialog = inject(NeoDialog);

  /**
   * Opens a confirmation dialog and returns its ref.
   *
   * `afterClosed()` always emits a value: dismissing through ESC, the backdrop or the close
   * button resolves to the cancel action's value (`'cancel'` by default).
   */
  public open<TValue extends string = ConfirmationResult>(
    config: ConfirmationModalConfig<TValue>,
  ): NeoDialogRef<ConfirmationModalComponent, TValue> {
    return this._dialog.open<ConfirmationModalComponent, ConfirmationModalConfig<TValue>, TValue>(
      ConfirmationModalComponent,
      {
        data: config,
        size: config.size ?? 'md',
        width: config.width,
        hasBackdrop: true,
        panelClass: CONFIRMATION_PANEL_CLASS,
        // Dismissal is owned by the shell so the result is never `undefined`; the CDK must not
        // close the dialog on its own.
        disableClose: true,
        role: config.confirm.color === 'danger' ? 'alertdialog' : 'dialog',
        ariaDescribedBy: `${config.autId}-description`,
        autoFocus: this._resolveAutoFocus(config),
        restoreFocus: true,
      },
    );
  }

  /**
   * Opens a confirmation dialog and resolves to `true` only when the confirm action was picked.
   */
  public async confirm<TValue extends string = ConfirmationResult>(
    config: ConfirmationModalConfig<TValue>,
  ): Promise<boolean> {
    const result = await firstValueFrom(this.open(config).afterClosed());

    return result === config.confirm.value;
  }

  /** Destructive delete confirmation (warning badge + red confirm button). */
  public delete(options: DeleteConfirmationOptions): Promise<boolean> {
    return this.confirm(deleteConfirmation(options));
  }

  /** Activate/deactivate (or enable/disable) confirmation. */
  public toggleActive(options: ToggleActiveConfirmationOptions): Promise<boolean> {
    return this.confirm(toggleActiveConfirmation(options));
  }

  /** "Your changes will be lost" confirmation. */
  public discard(options?: DiscardConfirmationOptions): Promise<boolean> {
    return this.confirm(discardConfirmation(options));
  }

  /** Informational dialog with a single action — the user cannot proceed. */
  public async blocked(options: BlockedConfirmationOptions): Promise<void> {
    await firstValueFrom(this.open(blockedConfirmation(options)).afterClosed());
  }

  /**
   * Focus the cancel button when there is one, so a destructive action is never the default
   * target of an accidental Enter. Falls back to the CDK's first tabbable element.
   */
  private _resolveAutoFocus<TValue extends string>(
    config: ConfirmationModalConfig<TValue>,
  ): string {
    if (config.cancel === false) return 'first-tabbable';

    const suffix = config.cancel?.autIdSuffix ?? 'cancel-btn';

    return `[aut-id="${config.autId}-${suffix}"]`;
  }
}
